// ADR 7.10.26w §7 — the companion's providers, and the one state above both surfaces.
//
// `AdaptiveFrame` is at its 50-line cap, so its two tutor providers moved here,
// joined by the app card's. Outermost first: `TutorDockProvider` (given the
// dock's `host`, for its toggle event), `TutorContextProvider`,
// `AppCardProvider`, and `CompanionState`, which sits below both so it can read
// the dock and the open card. It owns `front` and publishes one value that
// `ShellBody`, `CompanionContents` and `CompanionSwitcher` all read, so the
// wrapper's grid and the cell inside it cannot disagree about the dock.
//
// Imports no wasm: this module is on the boot graph (25.9.26f).

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { useSpreadValue, useWindowClassValue } from '../../layout/windowClassContext'
import { AppCardProvider, useAppCardState, type BarEntry } from '../apps/sheet/AppCardProvider'
import { TutorContextProvider } from '../tutor/context/TutorContextProvider'
import { TutorDockProvider, useTutorDock } from '../tutor/dock/TutorDockProvider'
import { dockScreen, type DockState } from '../tutor/dock/dockState'
import { frontAfter, shown as shownOf, type Front, type FrontChange, type Shown } from './companionFront'
import { cardHost, dockHost, type CardHost, type SurfaceHost } from './hosts'

export interface Companion {
  front: Front
  shown: Shown
  /** The switcher's choice; the caller emits `web_companion_switched`. */
  switchTo(front: Front): void
  /** Where the dock is drawn: `null` unless it is open on a dock route. */
  dockDrawn: SurfaceHost | null
  cardHost: CardHost
}

const NO_COMPANION: Companion = { front: 'tutor', shown: 'rest', switchTo: () => {}, dockDrawn: null, cardHost: 'sheet' }

/** Defaulted, not throwing: suites mount `ShellBody`'s readers without the frame. */
const CompanionContext = createContext<Companion>(NO_COMPANION)

export function useCompanion(): Companion {
  return useContext(CompanionContext)
}

const SWITCHED: Record<Front, FrontChange> = { tutor: 'switched_to_tutor', app_card: 'switched_to_card' }

/**
 * `front`, moved by the dock's any → `open` edge and the card's new-entry
 * edge. Both are detected **during render** against the previous values held
 * in state (React's "adjusting state when a prop changes"), never in an effect,
 * so no frame paints with a stale front.
 */
function useFront(dock: DockState, card: BarEntry | null): [Front, (to: Front) => void] {
  const [front, setFront] = useState<Front>('tutor')
  const [seen, setSeen] = useState({ dock, card })
  let next = front
  if (seen.dock !== dock || seen.card !== card) {
    if (dock === 'open' && seen.dock !== 'open') next = frontAfter(next, 'tutor_opened')
    if (card !== null && card !== seen.card) next = frontAfter(next, 'card_shown')
    setSeen({ dock, card })
    if (next !== front) setFront(next)
  }
  const switchTo = useMemo(() => (to: Front) => setFront((current) => frontAfter(current, SWITCHED[to])), [])
  return [next, switchTo]
}

function CompanionState({ host, children }: { host: SurfaceHost; children: ReactNode }) {
  const { state } = useTutorDock()
  const { open } = useAppCardState()
  const { pathname } = useLocation()
  const layout = useSpreadValue().layout
  const [front, switchTo] = useFront(state, open)
  // "Open" means drawable: a detached dock is in the other window.
  const tutor = state === 'open' && dockScreen(pathname) !== null
  const value = useMemo<Companion>(
    () => ({
      front,
      shown: shownOf(front, { tutor, card: open !== null }),
      switchTo,
      dockDrawn: tutor ? host : null,
      cardHost: cardHost(layout),
    }),
    [front, tutor, open, switchTo, host, layout],
  )
  return <CompanionContext.Provider value={value}>{children}</CompanionContext.Provider>
}

/**
 * `pane` is the whole window's, read here in `AdaptiveFrame`, above any
 * `PageWindowClass` (§2).
 */
export function CompanionProviders({ children }: { children: ReactNode }) {
  const host = dockHost(useSpreadValue().layout, useWindowClassValue().paneLayout)
  return (
    <TutorDockProvider host={host}>
      <TutorContextProvider>
        <AppCardProvider>
          <CompanionState host={host}>{children}</CompanionState>
        </AppCardProvider>
      </TutorContextProvider>
    </TutorDockProvider>
  )
}
